import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axiosInstance from "../../utils/axios/axiosConfig";
import { toast } from "react-toastify";
import {
  X, Plus, Minus, Trash, MagnifyingGlass,
  User, EnvelopeSimple, Phone, MapPin, Buildings,
} from "@phosphor-icons/react";
import { Input, Textarea, Select, Button } from "../../components/ui";
import { quoteTotals, lineNet, formatMAD } from "./quote";

const CUSTOMER_FIELDS = [
  { id: "first_name", label: "First name", type: "text", icon: User },
  { id: "last_name", label: "Last name", type: "text", icon: User },
  { id: "email", label: "Email", type: "email", icon: EnvelopeSimple },
  { id: "phone", label: "Phone", type: "tel", icon: Phone },
  { id: "city", label: "City", type: "text", icon: MapPin },
  { id: "company_name", label: "Company", type: "text", icon: Buildings },
];

const emptyForm = {
  status: "pending",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  city: "",
  company_name: "",
  comments: "",
  discount: 0,
  discount_type: "amount",
};

const EditOrderSidebar = ({ isOpen, onClose, order, onSaved }) => {
  const isCreate = !order?.id;
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    getProducts();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (order?.id) {
      setFormData({
        status: order.status || "pending",
        first_name: order.first_name || "",
        last_name: order.last_name || "",
        email: order.email || "",
        phone: order.phone || "",
        city: order.city || "",
        company_name: order.company_name || "",
        comments: order.comments || "",
        discount: Number(order.discount || 0),
        discount_type: order.discount_type || "amount",
      });
      setCart(order.products || []);
    } else {
      setFormData(emptyForm);
      setCart([]);
    }
    setSearchQuery("");
  }, [order, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const getProducts = async () => {
    try {
      const response = await axiosInstance.post(`/products/search`);
      if (response.data) setAvailableProducts(response.data.data);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error fetching products");
    }
  };

  const addToCart = (product) => {
    const existing = cart.find((i) => i.id === product.id && !i.pivot?.removed);
    if (existing) {
      setLineField(product.id, "quantity", (existing.pivot?.quantity || 0) + 1);
    } else {
      setCart((c) => [
        ...c,
        { ...product, pivot: { quantity: 1, price: product.price ?? 0, discount: 0, discount_type: "percent" } },
      ]);
    }
  };

  const setLineField = (productId, field, value) =>
    setCart((c) => c.map((i) => (i.id === productId ? { ...i, pivot: { ...i.pivot, [field]: value } } : i)));

  const updateQuantity = (productId, qty) => {
    if (qty < 1) return removeFromCart(productId);
    setLineField(productId, "quantity", qty);
  };

  const removeFromCart = (productId) =>
    setCart((c) => c.map((i) => (i.id === productId ? { ...i, pivot: { ...i.pivot, quantity: 0, removed: true } } : i)));

  const visibleCart = cart.filter((p) => !p.pivot?.removed);
  const { subtotal, globalDiscount, total } = quoteTotals({
    products: visibleCart,
    discount: formData.discount,
    discount_type: formData.discount_type,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name.trim()) return toast.warn("Customer name is required.");
    setLoading(true);
    try {
      const products = cart.map((p) => ({
        id: p.id,
        quantity: p.pivot?.quantity || 0,
        price: p.pivot?.price ?? null,
        discount: Number(p.pivot?.discount || 0),
        discount_type: p.pivot?.discount_type || "percent",
        remove: Boolean(p.pivot?.removed),
      }));

      let response;
      if (isCreate) {
        response = await axiosInstance.post(`/orders/store`, {
          ...formData,
          discount: Number(formData.discount || 0),
          products: products.filter((p) => !p.remove).map(({ remove, ...rest }) => rest),
        });
        toast.success("Quote created");
      } else {
        response = await axiosInstance.put(`/orders/update/${order.id}`, {
          ...formData,
          discount: Number(formData.discount || 0),
          products,
        });
        toast.success("Quote updated");
      }
      onSaved?.(response.data?.order || response.data);
      onClose?.();
    } catch (error) {
      const errs = error.response?.data?.errors;
      const first = errs ? Object.values(errs)[0]?.[0] : null;
      toast.error(first || error?.response?.data?.message || "Error saving quote");
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = availableProducts.filter((p) =>
    (p.name || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const lineInput = "h-9 w-full rounded-lg border border-line bg-surface px-2 text-sm text-ink outline-none focus:border-accent focus:ring-2 focus:ring-accent/30";

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="flex h-full w-full max-w-xl flex-col border-l border-line bg-surface shadow-pop"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-line px-6 py-4">
              <div>
                <h2 className="text-base font-semibold text-ink">{isCreate ? "New quote" : "Edit request"}</h2>
                <p className="text-xs text-muted">{isCreate ? "Create an internal devis request" : `#${order.order_number}`}</p>
              </div>
              <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink" aria-label="Close">
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <form id="quote-form" onSubmit={handleSubmit} className="flex-1 space-y-6 overflow-y-auto px-6 py-6">
              <Select
                label="Status"
                value={formData.status || null}
                onChange={(v) => setFormData((p) => ({ ...p, status: v || "" }))}
                options={[
                  { value: "pending", label: "New" },
                  { value: "quoted", label: "Quote sent" },
                  { value: "approved", label: "Approved" },
                  { value: "rejected", label: "Rejected" },
                  { value: "expired", label: "Expired" },
                  { value: "processing", label: "In progress" },
                  { value: "completed", label: "Completed" },
                  { value: "cancelled", label: "Cancelled" },
                ]}
              />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {CUSTOMER_FIELDS.map((f) => (
                  <Input key={f.id} label={f.label} icon={f.icon} type={f.type} name={f.id} value={formData[f.id] || ""} onChange={handleChange} />
                ))}
              </div>

              <Textarea label="Comments" name="comments" rows={2} value={formData.comments} onChange={handleChange} placeholder="Internal notes / appears on the devis…" />

              {/* Items */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-sm font-medium text-ink/90">Items</label>
                  <span className="text-xs text-muted">{visibleCart.length} item(s)</span>
                </div>
                {visibleCart.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-line bg-surface-2 px-3 py-6 text-center text-sm text-faint">No items — add from the list below.</p>
                ) : (
                  <ul className="space-y-2">
                    {cart.map((product) =>
                      product.pivot?.removed ? null : (
                        <li key={product.id} className="rounded-xl border border-line bg-surface-2 p-3">
                          <div className="flex items-center gap-3">
                            <img src={product.images?.[0] || "/placeholder.svg"} alt="" className="h-10 w-10 rounded-lg border border-line object-cover" />
                            <p className="min-w-0 flex-1 truncate text-sm font-medium text-ink">{product.name}</p>
                            <span className="text-sm font-semibold text-ink">{formatMAD(lineNet(product))}</span>
                            <button type="button" onClick={() => removeFromCart(product.id)} className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-red-500/10 hover:text-red-600" aria-label="Remove">
                              <Trash size={15} />
                            </button>
                          </div>
                          <div className="mt-2.5 grid grid-cols-3 gap-2">
                            <div>
                              <label className="mb-0.5 block text-[11px] text-muted">Unit price</label>
                              <input type="number" step="0.01" min={0} value={product.pivot?.price ?? 0} onChange={(e) => setLineField(product.id, "price", e.target.value)} className={lineInput} />
                            </div>
                            <div>
                              <label className="mb-0.5 block text-[11px] text-muted">Quantity</label>
                              <div className="flex items-center">
                                <button type="button" onClick={() => updateQuantity(product.id, (product.pivot?.quantity || 1) - 1)} className="grid h-9 w-8 shrink-0 place-items-center rounded-l-lg border border-line bg-surface text-muted hover:text-ink"><Minus size={13} /></button>
                                <input type="number" min={1} value={product.pivot?.quantity || 1} onChange={(e) => updateQuantity(product.id, Math.max(1, parseInt(e.target.value || 1, 10)))} className="h-9 w-full border-y border-line bg-surface text-center text-sm text-ink outline-none" />
                                <button type="button" onClick={() => updateQuantity(product.id, (product.pivot?.quantity || 0) + 1)} className="grid h-9 w-8 shrink-0 place-items-center rounded-r-lg border border-line bg-surface text-muted hover:text-ink"><Plus size={13} /></button>
                              </div>
                            </div>
                            <div>
                              <label className="mb-0.5 block text-[11px] text-muted">Remise</label>
                              <div className="flex">
                                <input
                                  type="number"
                                  min={0}
                                  value={product.pivot?.discount ?? 0}
                                  onChange={(e) => {
                                    const isPct = (product.pivot?.discount_type || "percent") === "percent";
                                    let v = Math.max(0, Number(e.target.value || 0));
                                    if (isPct) v = Math.min(100, v);
                                    setLineField(product.id, "discount", v);
                                  }}
                                  className="h-9 w-full rounded-l-lg border border-line bg-surface px-2 text-sm text-ink outline-none focus:z-10 focus:border-accent focus:ring-2 focus:ring-accent/30"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    setLineField(product.id, "discount_type", (product.pivot?.discount_type || "percent") === "percent" ? "amount" : "percent")
                                  }
                                  className="grid h-9 w-12 shrink-0 place-items-center rounded-r-lg border border-l-0 border-line bg-surface-2 text-xs font-semibold text-muted transition hover:text-ink"
                                  title="Switch between % and MAD"
                                >
                                  {(product.pivot?.discount_type || "percent") === "percent" ? "%" : "MAD"}
                                </button>
                              </div>
                            </div>
                          </div>
                        </li>
                      )
                    )}
                  </ul>
                )}
              </div>

              {/* Add products */}
              <div>
                <label className="mb-2 block text-sm font-medium text-ink/90">Add products</label>
                <Input icon={MagnifyingGlass} placeholder="Search products…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                <ul className="mt-3 max-h-56 space-y-2 overflow-y-auto">
                  {filteredProducts.map((product) => (
                    <li key={product.id} className="flex items-center gap-3 rounded-xl border border-line bg-surface-2 px-2 py-2">
                      <img src={product.images?.[0] || "/placeholder.svg"} alt="" className="h-10 w-10 rounded-lg border border-line object-cover" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-ink">{product.name}</p>
                        <p className="text-xs text-muted">{product.price != null ? formatMAD(product.price) : "—"}</p>
                      </div>
                      <button type="button" onClick={() => addToCart(product)} className="grid h-8 w-8 place-items-center rounded-lg bg-accent text-accent-fg transition hover:bg-accent-strong" aria-label="Add">
                        <Plus size={16} weight="bold" />
                      </button>
                    </li>
                  ))}
                  {!filteredProducts.length && <li className="px-1 py-2 text-sm text-faint">No products found.</li>}
                </ul>
              </div>

              {/* Global discount */}
              <div>
                <label className="mb-2 block text-sm font-medium text-ink/90">Global discount (remise)</label>
                <div className="grid grid-cols-2 gap-3">
                  <Select
                    value={formData.discount_type}
                    onChange={(v) => setFormData((p) => ({ ...p, discount_type: v || "amount" }))}
                    options={[
                      { value: "amount", label: "Amount (MAD)" },
                      { value: "percent", label: "Percent (%)" },
                    ]}
                  />
                  <Input type="number" min={0} name="discount" value={formData.discount} onChange={handleChange} placeholder="0" />
                </div>
              </div>

              {/* Totals */}
              <div className="rounded-xl border border-line bg-surface-2 p-3 text-sm">
                <div className="flex justify-between py-0.5">
                  <span className="text-muted">Subtotal</span>
                  <span className="text-ink">{formatMAD(subtotal)}</span>
                </div>
                {globalDiscount > 0 && (
                  <div className="flex justify-between py-0.5">
                    <span className="text-muted">Remise</span>
                    <span className="text-ink">- {formatMAD(globalDiscount)}</span>
                  </div>
                )}
                <div className="mt-1 flex justify-between border-t border-line pt-2 font-semibold">
                  <span className="text-ink">Total</span>
                  <span className="text-ink">{formatMAD(total)}</span>
                </div>
              </div>
            </form>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 border-t border-line px-6 py-4">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" form="quote-form" loading={loading}>
                {isCreate ? "Create quote" : "Save changes"}
              </Button>
            </div>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EditOrderSidebar;
